<template>
    <div class="documents_card">
        <div class="card_toolbar">
            <div class="search_wrap">
                <SearchIcon class="search_icon" />
                <input
                    class="search_input"
                    type="search"
                    :value="search"
                    placeholder="Buscar por número de documento"
                    @input="onSearchInput" />
            </div>
        </div>

        <div class="table_container" v-if="sortedDocuments.length > 0">
            <table class="documents_table">
                <thead>
                    <tr>
                        <th v-for="col in columns" :key="col.key"
                            :class="col.class"
                            :aria-sort="ariaSort(col)">
                            <button v-if="col.sortable"
                                type="button"
                                class="th_sort"
                                :class="{ 'is-active': sortKey === col.key }"
                                @click="sortBy(col.key)">
                                <span class="th_label">{{ col.label }}</span>
                                <SortArrowUp v-if="sortKey === col.key && sortDir === 'asc'"
                                    class="sort_icon" />
                                <SortArrowDown v-else-if="sortKey === col.key && sortDir === 'desc'"
                                    class="sort_icon" />
                                <SortArrowNo v-else class="sort_icon sort_icon--idle" />
                            </button>
                            <span v-else>{{ col.label }}</span>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="doc in paginatedDocuments"
                        :key="`row-${doc.td_id}`"
                        class="doc_row"
                        :class="{ 'is-cancelled': doc.cancelled }">
                        <td class="col_date">{{ formatDate(doc.emitted_at) }}</td>
                        <!-- Mes Devengo — réplica de la celda del clasificador
                            (ClassifyDocuments): sin devengo muestra el mes de
                            emisión + CalendarMinus; con devengo, init (y end si
                            difiere) + CalendarCheck. Display-only: editar el
                            devengo vive en el clasificador. -->
                        <td class="col_accrual">
                            <div v-if="!doc.has_accrual" class="accrual_cell">
                                <span class="accrual_month">{{ formatMonth(doc.emitted_at) }}</span>
                                <CalendarMinus class="accrual_icon" />
                            </div>
                            <div v-else class="accrual_cell">
                                <span class="accrual_month">
                                    <p>{{ formatMonth(doc.init_accrual) }}</p>
                                    <p v-if="doc.init_accrual != doc.end_accrual">
                                        {{ formatMonth(doc.end_accrual) }}
                                    </p>
                                </span>
                                <CalendarCheck class="accrual_icon" />
                            </div>
                        </td>
                        <!-- Las cargas -clf generan números de documento largos
                            que desbordan la columna: se truncan a los últimos 3
                            dígitos y el número completo vive en el tooltip. -->
                        <td class="col_docnum">
                            <span v-tooltip.top="docNumberTooltip(doc)">
                                {{ formatDocNumber(doc) }}
                            </span>
                        </td>
                        <td class="col_doctype">{{ formatDocType(doc.doc_type) }}</td>
                        <td class="col_amount_total">{{ formatMoney(totalAmount(doc)) }}</td>
                        <td class="col_account">
                            <!-- Anulados: mismo texto que el clasificador (NC que
                                anula, o "Anulada" para Boleta de Honorarios). -->
                            <span v-if="doc.cancelled" class="status_pill status_cancelled">
                                <template v-if="doc.doc_type == 'Boleta de Honorarios'">
                                    Anulada
                                </template>
                                <template v-else>
                                    Anulado por NC: {{ doc.nc_doc_number }}
                                </template>
                            </span>
                            <template v-else-if="doc.accounts && doc.accounts.length">
                                <span v-for="(acc, idx) in uniqueLevel3s(doc.accounts)"
                                    :key="`acc-${doc.td_id}-${idx}`"
                                    class="square_pill account_pill">
                                    {{ acc }}
                                </span>
                            </template>
                            <!-- Heredado de la plantilla de la contraparte
                                (accounts_doc): el ícono de clasificación
                                automática la distingue de la clasificación
                                por documento. -->
                            <template v-else-if="doc.accounts_doc && doc.accounts_doc.length">
                                <span class="auto_class_marker"
                                    title="Clasificación heredada de la contraparte">
                                    <RobotFace class="auto_class_icon" />
                                </span>
                                <span v-for="(acc, idx) in doc.accounts_doc"
                                    :key="`accdoc-${doc.td_id}-${idx}`"
                                    class="square_pill account_pill">
                                    {{ acc }}
                                </span>
                            </template>
                            <button v-else-if="canClassify"
                                class="classify_pill"
                                @click="goToClassifier(doc)">
                                <EditPencil class="edit_icon" />
                                Clasificar
                            </button>
                            <span v-else class="status_pill status_unclassified">
                                Sin Clasificar
                            </span>
                        </td>
                        <td class="col_categories">
                            <div v-for="(type, typeIdx) in categories"
                                :key="`attr-${doc.td_id}-${type}`"
                                class="category_group">
                                <template v-if="doc.attribute && doc.attribute[type]">
                                    <span class="category_type">{{ smartCapitalize(type) }}:</span>
                                    <span v-for="(attr, attrIdx) in dedupeAttributes(doc.attribute[type])"
                                        :key="`attr-${doc.td_id}-${type}-${attrIdx}`"
                                        :class="['category_pill', typeIdx === 0 ? 'category_pill_blue' : 'category_pill_purple']">
                                        {{ smartCapitalize(attr.name) }}
                                    </span>
                                </template>
                            </div>
                        </td>
                        <td class="col_action">
                            <button v-if="canClassify && !doc.cancelled"
                                class="edit_btn"
                                aria-label="Clasificar documento"
                                @click="goToClassifier(doc)">
                                <EditPencil class="edit_icon" />
                            </button>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <Pagination v-if="sortedDocuments.length > 0 && maxPages > 1"
            class="docs_pagination"
            v-model="actualPage"
            :maxPages="maxPages" />

        <p v-if="sortedDocuments.length === 0 && search" class="empty_state">
            Sin resultados para "{{ search }}".
        </p>
        <p v-else-if="sortedDocuments.length === 0" class="empty_state">
            Este cliente no tiene documentos en el mes seleccionado.
        </p>
    </div>
</template>

<script>
import moment from "moment";
import { mapState } from "vuex";

import { parseToMoney } from "@/utils/currency";
import { smartCapitalize } from "@/utils/string";

import Pagination from "@/components/generics/Pagination";
import EditPencil from "@/components/svg/EditPencil.vue";
import SearchIcon from "@/components/svg/SearchIcon.vue";
import RobotFace from "@/components/svg/RobotFace.vue";
import CalendarCheck from "@/components/svg/CalendarCheck.vue";
import CalendarMinus from "@/components/svg/CalendarMinus.vue";
import SortArrowUp from "@/components/svg/SortArrowUp.vue";
import SortArrowDown from "@/components/svg/SortArrowDown.vue";
import SortArrowNo from "@/components/svg/SortArrowNo.vue";

// Los docs de cargas internas llegan con la obligación cruda como doc_type
// ("remuneracion-clf"). Labels castellanos para las conocidas; una -clf
// nueva cae al fallback genérico de formatDocType (sin sufijo, capitalizado).
const CLF_DOC_TYPE_LABELS = {
    "remuneracion-clf": "Remuneración",
    "invoices-clf": "Factura Extranjera",
    "recibos-clf": "Recibo",
    "finiquitos-clf": "Finiquito",
    "notas-venta-clf": "Nota de Ingreso",
};

export default {
    name: "DocumentsDetailCard",

    components: {
        Pagination,
        EditPencil,
        SearchIcon,
        RobotFace,
        CalendarCheck,
        CalendarMinus,
        SortArrowUp,
        SortArrowDown,
        SortArrowNo,
    },

    props: {
        /** Lista filtrada por search vía v-model implícito (search + documents). */
        filteredDocuments: { type: Array, required: true },
        /** Counterpart cargado por openClientDetail (incluye ci_id, pay_or_charge, obligation_name). */
        counterpart: { type: Object, default: null },
        /** Search query (v-model con el store del padre — el padre hace bridge). */
        search: { type: String, default: "" },
        /** Categorías globales del classification store (tipos: tiendas, marcas, ...). */
        categories: { type: Array, default: () => [] },
        /**
         * Rango activo del drawer (value de RANGE_OPTIONS: last12/last24/todo).
         * Se propaga al clasificador al abrir un doc para que reabra en el mismo
         * período. Comparte vocabulario con el `periodDataSelect` del clasificador.
         */
        selectedRange: { type: String, default: null },
    },

    data() {
        return {
            // Definición de columnas — fuente única para el thead. El orden DEBE
            // espejar el de las celdas del tbody (las filas se escriben a mano).
            // `key` enlaza con el accessor de `sortValue`. Orden réplica de la
            // tabla del clasificador (ClassifyDocuments), conservando la columna
            // de monto total facturado propia del drawer.
            columns: [
                { key: "date", label: "Fecha Emisión", class: "col_date", sortable: true },
                { key: "accrual", label: "Mes Devengo", class: "col_accrual", sortable: true },
                { key: "docnum", label: "N° Doc.", class: "col_docnum", sortable: true },
                { key: "doctype", label: "Tipo Documento", class: "col_doctype", sortable: true },
                { key: "amount_total", label: "Monto total", class: "col_amount_total", sortable: true },
                { key: "account", label: "Cuenta", class: "col_account", sortable: true },
                { key: "categories", label: "Categorías", class: "col_categories", sortable: true },
                { key: "action", label: "", class: "col_action", sortable: false },
            ],
            // Orden activo. `sortKey` null = orden original del backend (sin tocar).
            sortKey: null,
            sortDir: "asc",
            actualPage: 1,
            itemsPerPage: 10,
        };
    },

    computed: {
        ...mapState("userAuth", ["user"]),

        // El lápiz/pill "Clasificar" navega al dashboard de clasificación —
        // sólo tiene sentido si el user tiene esa sección habilitada y la
        // contraparte es clasificable (finiquitos no tienen árbol).
        canClassify() {
            return Boolean(
                this.user && this.user.has_classificator &&
                this.counterpart && this.counterpart.obligation_name !== "finiquitos-clf",
            );
        },

        // Vista ordenada de los documentos, en los mismos buckets que el
        // clasificador (sortAllDocuments): sin clasificar primero (es el
        // trabajo pendiente), luego clasificados, anulados al final. Dentro
        // de cada bucket aplica el orden por columna activo; sin `sortKey`
        // se respeta el orden del backend.
        sortedDocuments() {
            const unclassified = [];
            const classified = [];
            const annulled = [];
            for (const doc of this.filteredDocuments) {
                if (doc.cancelled) {
                    annulled.push(doc);
                } else if (this.isDocClassified(doc)) {
                    classified.push(doc);
                } else {
                    unclassified.push(doc);
                }
            }
            return [
                ...this.sortBucket(unclassified),
                ...this.sortBucket(classified),
                ...this.sortBucket(annulled),
            ];
        },

        maxPages() {
            return Math.ceil(this.sortedDocuments.length / this.itemsPerPage) || 1;
        },

        paginatedDocuments() {
            const start = (this.actualPage - 1) * this.itemsPerPage;
            return this.sortedDocuments.slice(start, start + this.itemsPerPage);
        },
    },

    watch: {
        // Cambió el set de documentos (search o refetch por rango): volver a
        // la primera página para no quedar parado en una página vacía.
        filteredDocuments() {
            this.actualPage = 1;
        },
        maxPages(newMax) {
            if (this.actualPage > newMax) {
                this.actualPage = newMax;
            }
        },
    },

    methods: {
        smartCapitalize,
        // Click en un header ordenable: si ya es la columna activa, alterna
        // asc↔desc; si no, la activa arrancando ascendente.
        sortBy(key) {
            if (this.sortKey === key) {
                this.sortDir = this.sortDir === "asc" ? "desc" : "asc";
            } else {
                this.sortKey = key;
                this.sortDir = "asc";
            }
        },

        // Ordena un bucket sin mutar la prop. Desempate fijo por monto
        // prorrateado descendente; último criterio por td_id para que el
        // orden sea estable/determinista.
        sortBucket(docs) {
            if (!this.sortKey) return docs;
            const dir = this.sortDir === "asc" ? 1 : -1;
            return docs.slice().sort((a, b) => {
                let r = this.compareBy(this.sortKey, a, b) * dir;
                if (r === 0) r = (b.amount ?? 0) - (a.amount ?? 0);
                if (r === 0) r = (a.td_id ?? 0) - (b.td_id ?? 0);
                return r;
            });
        },

        // Valor comparable por columna. Números para fechas/montos/N° doc;
        // strings (minúsculas) para texto y para las columnas derivadas de
        // pills (cuenta madre / cuenta / categorías).
        sortValue(key, doc) {
            switch (key) {
                case "date":
                    return doc.emitted_at ? moment(doc.emitted_at).valueOf() : 0;
                case "accrual":
                    // Mes Devengo: inicio del devengo si existe; si no, la fecha
                    // de emisión (mismo dato que muestra la celda).
                    return moment(doc.has_accrual ? doc.init_accrual : doc.emitted_at).valueOf() || 0;
                case "doctype":
                    // Ordena por el label visible (no el crudo "-clf"), para
                    // que el orden coincida con lo que el usuario lee.
                    return this.formatDocType(doc.doc_type).toLowerCase();
                case "docnum": {
                    const n = Number(doc.doc_number);
                    return Number.isNaN(n) ? (doc.doc_number || "") : n;
                }
                case "amount_total":
                    return this.totalAmount(doc);
                case "account": {
                    // Coherente con lo que se muestra: clasificación por documento
                    // si existe, si no la heredada de la contraparte (accounts_doc).
                    const own = this.uniqueLevel3s(doc.accounts || []);
                    const names = own.length ? own : (doc.accounts_doc || []);
                    return names.join(", ").toLowerCase();
                }
                case "categories":
                    return this.categoryNames(doc).toLowerCase();
                default:
                    return "";
            }
        },

        // Compara dos docs por una columna: resta para números, localeCompare
        // (es, numeric) para strings — así "Tienda 2" < "Tienda 10".
        compareBy(key, a, b) {
            const x = this.sortValue(key, a);
            const y = this.sortValue(key, b);
            if (typeof x === "number" && typeof y === "number") return x - y;
            return String(x).localeCompare(String(y), "es", { numeric: true, sensitivity: "base" });
        },

        // Texto plano con los nombres de todos los atributos del doc, para
        // poder ordenar la columna Categorías (que en la UI son pills).
        categoryNames(doc) {
            const attr = doc.attribute || {};
            const names = [];
            Object.keys(attr).forEach((type) => {
                (attr[type] || []).forEach((it) => {
                    if (it && it.name) names.push(it.name);
                });
            });
            return names.join(", ");
        },

        // aria-sort del <th> para lectores de pantalla: "ascending"/"descending"
        // en la columna activa, "none" en las ordenables inactivas, ausente en
        // las no ordenables.
        ariaSort(col) {
            if (!col.sortable) return null;
            if (this.sortKey !== col.key) return "none";
            return this.sortDir === "asc" ? "ascending" : "descending";
        },

        onSearchInput(event) {
            this.$emit("update-search", event.target.value);
        },

        formatDate(value) {
            if (!value) return "";
            return moment(value).format("DD-MM-YYYY");
        },

        // Mismo formato de mes que el clasificador: "MMM-YYYY" sin el punto
        // de la abreviatura en español ("abr." → "abr"), capitalizado por CSS.
        formatMonth(value) {
            if (!value) return "";
            return moment(value).format("MMM-YYYY").replace(".", "");
        },

        formatMoney(value) {
            if (value == null) return "";
            return parseToMoney(value);
        },

        // Tipos de documento "de sistema" (sufijo -clf) se muestran con label
        // legible; los tipos reales del SII ("Factura afecta", ...) pasan tal
        // cual. Los checks de lógica (anulados, finiquitos) siguen usando el
        // valor crudo — esto es sólo presentación.
        formatDocType(value) {
            if (!this.isClfDocType(value)) return value || "";
            if (CLF_DOC_TYPE_LABELS[value]) return CLF_DOC_TYPE_LABELS[value];
            const base = value.slice(0, -"-clf".length);
            return base.charAt(0).toUpperCase() + base.slice(1);
        },

        isClfDocType(value) {
            return Boolean(value && value.endsWith("-clf"));
        },

        // N° Doc.: sólo se trunca en docs -clf (sus números son largos); los
        // documentos del SII muestran el folio completo.
        formatDocNumber(doc) {
            const num = doc.doc_number == null ? "" : String(doc.doc_number);
            if (!this.isClfDocType(doc.doc_type) || num.length <= 3) return num;
            return `...${num.slice(-3)}`;
        },

        // Tooltip estándar de texto completo (v-tooltip): sólo cuando la celda
        // está truncada, así no hay tooltips redundantes en folios cortos.
        docNumberTooltip(doc) {
            const num = doc.doc_number == null ? "" : String(doc.doc_number);
            return this.formatDocNumber(doc) === num ? "" : num;
        },

        // El backend devuelve `amount` ya prorrateado al mes y conserva el
        // monto facturado original en `original_amount`. Si esa key falta
        // (datos legacy o algún edge case), caemos al prorrateado mismo —
        // peor caso ambas columnas muestran lo mismo, mejor que un blank.
        totalAmount(doc) {
            if (doc == null) return 0;
            return doc.original_amount != null ? doc.original_amount : doc.amount;
        },

        // `doc.accounts` viene como [{ level_3, level_2, level_1, percentage, ... }]
        // con posibles duplicados por prorrateo. La tabla muestra cada level_3
        // una sola vez (la card del usuario es visualizar, no inspeccionar el
        // detalle de splits — eso vive en el árbol del clasificador).
        uniqueLevel3s(accounts) {
            const seen = new Set();
            const result = [];
            for (const acc of accounts) {
                if (!acc.level_3 || seen.has(acc.level_3)) continue;
                seen.add(acc.level_3);
                result.push(acc.level_3);
            }
            return result;
        },

        // Atributos en `doc.attribute[type]` también pueden duplicarse por
        // splits (Tienda 1 al 80% y 20% en dos prorrateos). El chip es
        // representación binaria — el cuántos detalles ya viven en el árbol.
        dedupeAttributes(items) {
            const seen = new Set();
            return (items || []).filter((it) => {
                if (!it || seen.has(it.name)) return false;
                seen.add(it.name);
                return true;
            });
        },

        // Clasificado = con prorrateo propio o heredado de la plantilla de la
        // contraparte. Mismo criterio de bucketing que el clasificador usa
        // tras su enriquecimiento en Vuex.
        isDocClassified(doc) {
            const own = doc.accounts && doc.accounts.length;
            const inherited = doc.accounts_doc && doc.accounts_doc.length;
            return Boolean(own || inherited);
        },

        // Clasificar un documento vive en el dashboard de clasificación: acá
        // sólo cerramos la pila de drawers y navegamos con los identificadores
        // del doc en la query. DashboardClassificator los consume (abre el
        // modal de la contraparte y expande el árbol del doc) y los limpia.
        goToClassifier(doc) {
            if (!this.canClassify || !this.counterpart) return;
            const query = {
                ...this.$route.query,
                section: "classificator",
                classify_ci_id: String(this.counterpart.ci_id),
                classify_td_id: String(doc.td_id),
                classify_pay_or_charge: this.counterpart.pay_or_charge,
                classify_obligation: this.counterpart.obligation_name || "Otros",
            };
            // El clasificador filtra documentos por fecha de EMISIÓN — pasamos
            // emitted_at (no el mes devengado) para que elija un rango que
            // contenga al doc.
            if (doc.emitted_at) {
                query.classify_doc_date = moment(doc.emitted_at).format("YYYY-MM-DD");
            }
            // Rango activo del drawer: el clasificador reabre en este mismo
            // período si contiene la emisión del doc; si no, expande al rango
            // más chico que lo contenga (consumeClassifyDeepLink). Comparten el
            // value (last12/last24/todo).
            if (this.selectedRange) {
                query.classify_range = this.selectedRange;
            }
            // `tab` pertenece a la sección de reportes; el clasificador no
            // tiene tabs.
            delete query.tab;
            this.closeModalStack();
            this.$router.push({ query }).catch(() => {});
        },

        // Cierra explícitamente los BModal de la cadena de padres (modal 2 y,
        // si el drill vino del análisis del mes, también modal 1). El cambio
        // de sección ya destruye los modales vía v-if, pero `close()` además
        // dispara la animación y los eventos `closed` de cada caller.
        closeModalStack() {
            let node = this.$parent;
            while (node) {
                if (node.$options && node.$options.name === "BModal" && typeof node.close === "function") {
                    node.close();
                }
                node = node.$parent;
            }
        },
    },
};
</script>

<style scoped lang="scss">
.documents_card {
    background: var(--report-bg);
    border: 1px solid var(--report-border);
    border-radius: 12px;
    box-shadow: var(--report-shadow-card);
    overflow: hidden;
}

.card_toolbar {
    padding: 16px 20px;
    border-bottom: 1px solid var(--report-border);
    display: flex;
    align-items: center;
    gap: 16px;
}

.search_wrap {
    position: relative;
    flex: 1;
    max-width: 360px;
}

.search_icon {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    width: 16px;
    height: 16px;
    color: var(--report-text-muted-soft);
    pointer-events: none;
}

.search_input {
    width: 100%;
    height: 40px;
    padding: 0 12px 0 38px;
    border: 1px solid var(--report-border);
    border-radius: 999px;
    background: var(--report-bg);
    font-size: 14px;
    color: var(--report-text);
    box-sizing: border-box;

    &::placeholder {
        color: var(--report-text-muted-soft);
    }

    &:focus {
        outline: none;
        border-color: var(--report-accent-text);
        box-shadow: 0 0 0 3px var(--report-accent-bg);
    }
}

.table_container {
    width: 100%;
    overflow-x: auto;
}

.documents_table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;

    // Celdas de la grilla centradas (header + filas) para lectura simétrica
    // fila a fila.
    thead th,
    .doc_row > td {
        text-align: center;
    }

    th {
        font-size: 12px;
        font-weight: 700;
        color: var(--report-text-muted-strong);
        padding: 12px 12px;
        border-bottom: 1px solid var(--report-border);
        background: var(--report-bg);
        white-space: nowrap;
    }

    td {
        padding: 14px 12px;
        border-bottom: 1px solid var(--report-divider-soft);
        color: var(--report-text);
        vertical-align: middle;
    }
}

// Header ordenable: el label + el glifo de sort viven en un botón que hereda
// la tipografía del th. Sin chrome propio; el label se aviva en hover/activo y
// el ícono comunica el estado (idle = doble flecha tenue, activo = una flecha).
.th_sort {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin: 0 auto;
    padding: 2px 4px;
    background: transparent;
    border: none;
    border-radius: 6px;
    font: inherit;
    color: inherit;
    cursor: pointer;
    white-space: nowrap;
    transition: color 120ms ease, background-color 120ms ease;

    &:hover {
        color: var(--report-text);
        background: var(--report-surface);
    }

    &:focus-visible {
        outline: 2px solid var(--report-accent-border);
        outline-offset: 2px;
    }

    &.is-active {
        color: var(--report-text);
    }
}

.sort_icon {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
    display: block;
}

// Estado inactivo: el glifo neutro (doble flecha) se atenúa para señalar
// "ordenable" sin competir con el contenido. El activo va a opacidad plena.
.sort_icon--idle {
    opacity: 0.4;
}

.doc_row {
    transition: background-color 120ms ease;

    &:hover {
        background: var(--report-surface);
    }

    &.is-cancelled {
        opacity: 0.6;
    }
}

.col_doctype,
.col_account,
.col_categories {
    min-width: 120px;
}

.col_docnum {
    width: 70px;
}

// Monto total facturado del documento. Color de texto principal: es la única
// magnitud monetaria de la tabla.
.col_amount_total {
    font-weight: 500;
    white-space: nowrap;
    width: 120px;
    color: var(--report-text);
}

// El lápiz tiene que quedar visible en cualquier ancho de pantalla: si la
// tabla excede el contenedor (overflow-x del table_container), la columna de
// acción se pega al borde derecho del scroll. Necesita fondo propio para que
// las celdas que pasan por debajo no se transparenten.
.col_action {
    width: 40px;
    position: sticky;
    right: 0;
    z-index: 1;
    background: var(--report-bg);
}

.doc_row:hover .col_action {
    background: var(--report-surface);
}

// Mes Devengo: mes(es) + ícono de estado de devengo, mismo lenguaje que la
// tabla del clasificador (CalendarMinus = sin devengo, CalendarCheck = con).
.accrual_cell {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    white-space: nowrap;
}

.accrual_month {
    text-transform: capitalize;

    p {
        margin: 0;
        line-height: 18px;
    }
}

.accrual_icon {
    flex-shrink: 0;
}

%pill_inline {
    display: inline-flex;
    align-items: center;
    padding: 2px 10px;
    font-size: 12px;
    font-weight: 500;
    border: 1px solid transparent;
    white-space: nowrap;
}

// Pills cuadradas de cuentas (radio chico = dato estructural), como en el
// clasificador: Cuenta Madre en surface gris, Cuenta en blanco con borde.
.square_pill {
    @extend %pill_inline;
    border-radius: 6px;
    margin: 1px 4px 1px 0;
}

.account_pill {
    background: var(--report-bg);
    color: var(--report-text-muted-strong);
    border-color: var(--report-border);
}

// Marcador de clasificación heredada de la contraparte (automática). Ícono
// tenue que distingue la clasificación por plantilla de la por documento.
.auto_class_marker {
    display: inline-flex;
    align-items: center;
    color: var(--report-text-muted);
    vertical-align: middle;
    cursor: help;
}

.auto_class_icon {
    width: 15px;
    height: 15px;
}

.status_pill {
    @extend %pill_inline;
    border-radius: 6px;
}

.status_unclassified {
    background: var(--report-surface);
    color: var(--report-text-muted-strong);
    border-color: var(--report-border);
}

.status_cancelled {
    background: var(--report-negative-bg);
    /* #b42318: rojo más oscuro que --report-negative (mejor contraste sobre el
       tint); one-off sin token semántico exacto. */
    color: #b42318;
    border-color: var(--report-negative-border);
}

// CTA "Clasificar" para docs sin clasificación — mismo lenguaje de alerta
// suave que el clasificador (tinte negativo + lápiz), sobre tokens del theme.
.classify_pill {
    @extend %pill_inline;
    border-radius: 8px;
    padding: 6px 12px;
    gap: 4px;
    font-weight: 600;
    background: var(--report-negative-bg);
    border-color: var(--report-negative-border);
    /* Mismo one-off documentado en .status_cancelled. */
    color: #b42318;
    cursor: pointer;
    transition: filter 120ms ease;

    &:hover {
        filter: brightness(0.97);
    }
}

.category_group {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
    gap: 4px;
    margin-bottom: 4px;

    &:last-child {
        margin-bottom: 0;
    }
}

.category_type {
    font-size: 12px;
    color: var(--report-text-muted);
    margin-right: 4px;
}

.category_pill {
    @extend %pill_inline;
    border-radius: 12px;
    font-size: 12px;
    padding: 1px 8px;
}

// Identidad cromática por tipo de categoría (1° azul, 2° violeta), igual que
// las circle pills del clasificador. One-offs ya documentados del feature.
.category_pill_blue {
    background: #eff6ff;
    color: #1e40af;
    border-color: #bfdbfe;
}

.category_pill_purple {
    background: #f5f3ff;
    color: #5b21b6;
    border-color: #ddd6fe;
}

.edit_btn {
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 4px;
    border-radius: 6px;
    color: var(--report-text-muted-strong);
    transition: background-color 120ms ease, color 120ms ease;

    &:hover {
        background: var(--report-surface);
        color: var(--report-text);
    }
}

.edit_icon {
    width: 16px;
    height: 16px;
}

.docs_pagination {
    padding: 12px 20px;
    border-top: 1px solid var(--report-divider-soft);
}

.empty_state {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--report-text-muted);
    font-size: 13px;
    margin: 0;
    padding: 32px;
}
</style>
